import React from 'react'
import PropTypes from 'prop-types'
import { Box, Drawer, Clipped, Button, Icon, IconManager, subscriptionsLogoPack, paymentMethodsIconPack, Layout, Level, List, Media, MediaQuery, Price, Text, ThemeProvider, Title, Sticky } from '@deseretbook/react-ui'
import { usePopup } from '@deseretbook/react-utils'
import ConfirmationSidebarContent from './ConfirmationSidebarContent'
import ShipmentContent from './ShipmentContent'

IconManager.register(paymentMethodsIconPack, subscriptionsLogoPack)

/** Map of card names to relevant icon name. */
const cardIcons = {
  'american express': 'ccAmex',
  master: 'ccMastercard',
  visa: 'ccVisa',
  discover: 'ccDiscover',
}

/**
 * Order confirmation page content
 *
 * @returns {function} Component
 */
const ConfirmationPage = ({
  order,
  user,
  payments,
  recentlyCompleted,
}) => {
  /** If order summary drawer is open or closed */
  const popup = usePopup()

  /** Format completedAt date to the browser's default locale */
  const date = new Date(order.completedAt)
  const options = { year: 'numeric', month: 'short', day: 'numeric' }
  const completedAt = date.toLocaleDateString(undefined, options)

  /** Filter shipments based on types */
  const digitalShipments = order.shipments.filter(shipment => shipment['digital?'])
  const storePickups = order.shipments.filter(({ storePickup }) => storePickup)
  const physicalShipments = order.shipments
    .filter(shipment => !shipment['digital?'] && !shipment.storePickup)

  /*
  * Send tapfiliate request and track Facebook purchase if order is recently completed.
  * Adding the [] just makes it run only once after the intial render
  */
  if (recentlyCompleted) {
    React.useEffect(() => {
      if (typeof window.tap === 'function') {
        tap('conversion', order.number, order.total)
      }

      if (window.fbq && typeof window.fbq === 'function') {
        window.fbq('track', 'Purchase', {
          content_ids: order.lineItems.map(({ variant }) => variant.sku),
          content_type: 'product',
          value: order.total,
          currency: 'USD'})
      }
    }, [])
  }

  return (
    <ThemeProvider>
      <MediaQuery.Medium>
        {isMediumSized => (
          <Layout>
            <Layout.Content>
              <Layout.Section>
                <Level>
                  <Level.Item>
                    <Title
                      title={recentlyCompleted
                        ? 'Thank you for your order'
                        : 'Order Details'
                      }
                      subtitle={!recentlyCompleted
                        ? `Placed on ${completedAt}`
                        : null
                      }
                      size={1}
                      aria-level={1}
                    />
                  </Level.Item>
                  {/* Print Button if screen is medium size or larger */}
                  {isMediumSized && (
                    <Level.Right>
                      <Level.Item>
                        <Button
                          type="button"
                          icon="printer"
                          color="light"
                          aria-label="print order details"
                          onClick={window.print}
                          title="Print order details"
                          className="hidden-print"
                        />
                      </Level.Item>
                    </Level.Right>
                  )}
                </Level>
                {recentlyCompleted && (
                  <Text className="hidden-print">
                    We'll send a confirmation email to <Text isItalic>{order.email}</Text>
                  </Text>
                )}
                {/* Shipment info and contents in this order: digital, store pickup, shipments */}
                {digitalShipments && (
                  <ShipmentContent shipAddress={order.shipAddress} shipments={digitalShipments} />
                )}
                {storePickups && (
                  <ShipmentContent shipAddress={order.shipAddress} shipments={storePickups} />
                )}
                {physicalShipments && (
                  <ShipmentContent shipAddress={order.shipAddress} shipments={physicalShipments} />
                )}
              </Layout.Section>
            </Layout.Content>
            {isMediumSized && (
              <Layout.Aside>
                <Layout.Section>
                  <ConfirmationSidebarContent
                    order={order}
                    payments={payments}
                    recentlyCompleted={recentlyCompleted}
                    user={user}
                  />
                </Layout.Section>
              </Layout.Aside>
            )}
            {/* Order summary bottom sticky when smaller than medium screen */}
            {!isMediumSized && (
              <Sticky position="bottom" background="alternate">
                <Box padding={0.5} flex={0}>
                  <Level>
                    <Level.Left isShrinkable>
                      <Clipped>
                        <Text>Total </Text>
                        <Text isBold><Price isBold value={order.total} /></Text>
                        <br />
                        <List>
                          {payments.map(payment => (
                            <List.Item>
                              <Media>
                                <Media.Figure>
                                  <Icon name={cardIcons[(payment.source.ccType || '').toLowerCase()] || 'ccGeneric'} size="medium" />
                                </Media.Figure>
                                <Media.Content>
                                  <div>
                                    <Text hasEllipsis size="small">
                                      Ends with {payment.source.lastDigits}
                                    </Text>
                                  </div>
                                </Media.Content>
                              </Media>
                            </List.Item>
                          ))}
                        </List>
                      </Clipped>
                    </Level.Left>
                    <Level.Right>
                      <Button type="button" size="small" {...popup.triggerProps}>Order Summary</Button>
                    </Level.Right>
                  </Level>
                  <Drawer {...popup.popupProps}>
                    <Drawer.Header>
                      <Text isBold>Order Summary</Text>
                    </Drawer.Header>
                    <Drawer.Content>
                      <ConfirmationSidebarContent
                        order={order}
                        payments={payments}
                        recentlyCompleted={recentlyCompleted}
                        user={user}
                      />
                    </Drawer.Content>
                  </Drawer>
                </Box>
              </Sticky>
            )}
          </Layout>
        )}
      </MediaQuery.Medium>
    </ThemeProvider>
  )
}

ConfirmationPage.propTypes = {
  order: PropTypes.shape({
    number: PropTypes.string,
    completedAt: PropTypes.string,
    email: PropTypes.string,
    shipAddress: PropTypes.shape({}),
    shipments: PropTypes.arrayOf(PropTypes.shape({})),
    lineItems: PropTypes.arrayOf(PropTypes.shape({
      variant: PropTypes.shape({
        sku: PropTypes.string,
      }),
    })),
    total: PropTypes.string,
  }).isRequired,
  payments: PropTypes.arrayOf(PropTypes.shape({})),
  recentlyCompleted: PropTypes.bool,
  user: PropTypes.shape({}),
}

ConfirmationPage.defaultProps = {
  payments: [],
  recentlyCompleted: false,
  user: {},
}

export default ConfirmationPage
